"use client";

import { useContext, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { CheckCircle2, Star } from "lucide-react";
import { ConversationsService } from "@/generated";
import { PublicConversationService } from "@/services/public/Conversation";
import { ChatWidgetContext } from "./NewAndIncredibleChatWidget";
import { cn } from "@/lib/utils";
import { Button } from "../shadcn/button";
import { Textarea } from "../shadcn/textarea";

/**
 * Inline CSAT form rendered for `data-csat_request` parts.
 *
 * Pushed into the stream by the agent's `request_customer_feedback` tool
 * (or the debug trigger). Submits a 1-5 rating + optional comment to the
 * conversation CSAT endpoint, which stores it on `conversations.feedback`
 * and writes a CSAT_RESPONSE audit event. "Maybe later" defers instead —
 * the form is dismissed and `conversations.feedback.deferred_at` is stamped
 * so it won't be re-requested.
 */
export function CSATFeedbackWidget({
  groupId,
  conversationId,
}: {
  groupId: string;
  conversationId: string;
}) {
  const t = useTranslations("widget.csat");
  const { publicAuth } = useContext(ChatWidgetContext);
  const [rating, setRating] = useState<number>(0);
  const [hovered, setHovered] = useState<number>(0);
  const [comment, setComment] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [deferred, setDeferred] = useState(false);

  const submitMutation = useMutation({
    mutationFn: async () => {
      if (publicAuth) {
        return PublicConversationService.addCsatResponse({
          ...publicAuth,
          conversationId,
          rating,
          comment: comment.trim() || null,
        });
      }
      return await ConversationsService.addCsatResponse({
        path: {
          group_id: groupId,
          conversation_id: conversationId,
        },
        body: {
          rating,
          comment: comment.trim() || null,
        },
      });
    },
    onSuccess: () => setSubmitted(true),
  });

  const deferMutation = useMutation({
    mutationFn: async () => {
      if (publicAuth) {
        return PublicConversationService.deferCsatResponse({
          ...publicAuth,
          conversationId,
        });
      }
      return await ConversationsService.deferCsatResponse({
        path: {
          group_id: groupId,
          conversation_id: conversationId,
        },
      });
    },
    onSuccess: () => setDeferred(true),
  });

  if (submitted) {
    return (
      <div className="flex items-center gap-2 rounded-lg border bg-muted/30 px-4 py-3 my-2 text-sm text-muted-foreground">
        <CheckCircle2 className="size-4 text-success-600" />
        {t("thanks")}
      </div>
    );
  }

  if (deferred) {
    return null;
  }

  return (
    <div className="flex flex-col gap-3 rounded-lg border bg-muted/30 px-4 py-3 my-2 max-w-md">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium">{t("question")}</span>
        <Button
          variant="ghost"
          size="sm"
          className="h-auto px-2 py-1 text-xs text-muted-foreground"
          onClick={() => deferMutation.mutate()}
          disabled={deferMutation.isPending}
        >
          {t("maybeLater")}
        </Button>
      </div>
      <div className="flex gap-1" onMouseLeave={() => setHovered(0)}>
        {[1, 2, 3, 4, 5].map((value) => (
          <button
            key={value}
            type="button"
            aria-label={t("rate", { value })}
            onClick={() => setRating(value)}
            onMouseEnter={() => setHovered(value)}
            className="p-0.5"
          >
            <Star
              className={cn(
                "size-6 text-muted-foreground/40 transition-colors",
                (hovered || rating) >= value &&
                  "fill-yellow-400 text-yellow-400"
              )}
            />
          </button>
        ))}
      </div>
      {rating > 0 && (
        <>
          <Textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={t("placeholder")}
            className="min-h-16 text-sm"
          />
          <div className="flex justify-end">
            <Button
              size="sm"
              onClick={() => submitMutation.mutate()}
              disabled={submitMutation.isPending}
            >
              {submitMutation.isPending ? t("submitting") : t("submit")}
            </Button>
          </div>
          {submitMutation.isError && (
            <span className="text-xs text-destructive">{t("submitError")}</span>
          )}
        </>
      )}
    </div>
  );
}
